import { Head, router } from "@inertiajs/react"
import { Pencil, Trash2 } from "lucide-react"

import { columns } from "@/components/accounts/columns"
import { DataTable } from "@/components/ui/data-table"
import AdminLayout from "@/layouts/admin-layout"
import {
  adminAccountPath,
  adminAccountsPath,
  editAdminAccountPath,
} from "@/routes"
import type { BreadcrumbItem, Pagy } from "@/types"
import type { Account } from "@/types"

interface AccountsProps {
  accounts: Account[]
  pagy: Pagy
  sort: string | null
  q: {
    name_cont?: string
    state_in?: string[]
  }
}

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Accounts",
    href: adminAccountsPath(),
  },
]

export default function Accounts({ accounts, pagy, sort }: AccountsProps) {
  const handleDelete = (account: Account) => {
    router.delete(adminAccountPath({ id: account.uid }), {
      preserveState: true,
      preserveScroll: true,
    })
  }

  return (
    <AdminLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />
      <DataTable
        columns={columns}
        data={accounts}
        pagy={pagy}
        sort={sort}
        onSortChange={(newSort) => {
          router.visit(adminAccountsPath(), {
            data: { sort: newSort },
            preserveState: true,
            preserveScroll: true,
          })
        }}
        onPageChange={(page) => {
          router.visit(adminAccountsPath(), {
            data: { page: page, sort: sort ?? undefined },
            preserveState: true,
            preserveScroll: true,
          })
        }}
        emptyComponent={
          <div className="text-muted-foreground py-10 text-center">
            No accounts found.
          </div>
        }
        actions={[
          {
            label: "Edit",
            icon: <Pencil className="h-4 w-4" />,
            onClick: (account: Account) => {
              router.visit(editAdminAccountPath({ id: account.uid }))
            },
            variant: "default",
          },
          {
            label: "Delete",
            icon: <Trash2 className="h-4 w-4" />,
            onClick: handleDelete,
            variant: "destructive",
            disabled: (account: Account) => account.deletable === false,
            disabledReason:
              "This account has billing records (subscriptions or invoices) and can't be deleted.",
          },
        ]}
      />
    </AdminLayout>
  )
}
